// @flow
import { flowRight, noop } from 'lodash/fp';
import { call, put, takeLatest, select, all } from 'redux-saga/effects';
import { map, toJS } from '~/core/utils/immutable';
import { save } from '~/core/api/product-groups';
import { generalErrorMessages as errorMessages } from '~/core/constants/errors';
import { alerts } from '~/core/constants';
import { dashboardAddProduct, dashboardRemoveProducts, saveDashboardEdit } from '~/core/state/dashboard/actions';
import { requestAlert } from '~/core/state/alerts/actions';
import handleApi from '~/core/state/handle-api';
import { pollNodeExists } from '~/core/state/honeycomb/actions';
import { getProductsSaga } from '~/core/state/products/sagas';
import { isDashboardCustomised } from '~/core/state/dashboard/sagas';
import handleSaga from '~/core/state/handle-saga';
import { saveGroupComplete, saveGroupPending, makeSaveNewGroupComplete } from './actions';
import { SAVE_GROUP, SAVE_NEW_GROUP_COMPLETE } from './constants';
import { forBeekeeper } from './utils';
import { getGroupConsumers } from './selectors';
import { getAvailableProductsForCustomisation } from '~/core/state/dashboard/selectors';
import type { Effect } from 'redux-saga';
import { customisationErrors } from './intl';

function* saveGroupSaga({
    payload: { id, name, availableItems, assignedToGroupItems }
}): Generator<noop | Effect, *, *> {
    yield put(saveGroupPending());
    const [res, error] = yield call(
        handleApi(save),
        forBeekeeper({ id, name, items: [...availableItems, ...assignedToGroupItems] })
    );

    if (error && error.status === 409) {
        yield put(requestAlert(customisationErrors.conflictStatusError, alerts.types.ERROR));
    } else if (error) {
        yield put(requestAlert(errorMessages.tryAgain, alerts.types.ERROR));
        return;
    }

    const { poll, consumers } = res;
    const groupConsumers = yield select(getGroupConsumers, { id });
    const availableDashboardProducts = yield select(getAvailableProductsForCustomisation);

    // Get the list of products that have been deselected from the group and are not already on the dashboard
    const deselectedProducts = groupConsumers.map(groupConsumers =>
        groupConsumers
            .toSet()
            .subtract(consumers)
            .intersect(availableDashboardProducts)
    );

    const deselectedSize = deselectedProducts.map(x => x.size).getOrElse(0);

    if (deselectedSize > 0) {
        const addProduct = flowRight(put, dashboardAddProduct);
        const addAllDevicesToDashboard = flowRight(all, toJS, map(addProduct));
        yield addAllDevicesToDashboard(deselectedProducts.getOrElse(null));
        yield put(saveDashboardEdit());
    }

    poll
        ? yield put(
              pollNodeExists({
                  id: poll,
                  nextAction: makeSaveNewGroupComplete({ name, consumers }),
                  errorAction: () => requestAlert(errorMessages.tryAgain, alerts.types.ERROR)
              })
          )
        : yield all([
              put(saveGroupComplete({ name, consumers, nodeId: id })),
              put(dashboardRemoveProducts({ ids: consumers })),
              isDashboardCustomised() && put(saveDashboardEdit())
          ]);
}

function* saveNewGroupCompleteSaga({ payload: { id, name, consumers } }) {
    yield getProductsSaga();
    yield put(dashboardAddProduct(id));
    yield put(dashboardRemoveProducts({ ids: consumers }));
    yield isDashboardCustomised() && (yield put(saveDashboardEdit()));
}

function* watchSaveGroupSaga(): Generator<noop | Effect, *, *> {
    yield takeLatest(SAVE_GROUP, handleSaga(saveGroupSaga));
}

function* watchSaveNewGroupCompleteSaga(): Generator<noop | Effect, *, *> {
    yield takeLatest(SAVE_NEW_GROUP_COMPLETE, handleSaga(saveNewGroupCompleteSaga));
}

export default [watchSaveGroupSaga, watchSaveNewGroupCompleteSaga];



// WEBPACK FOOTER //
// ./src/core/state/groups/sagas.js